// SPDX-License-Identifier: AGPL-3.0-or-later
// The category tree. An id is its path: "housing", "housing/utilities",
// "housing/utilities/water". A transaction can sit at any level, and totals
// roll up to the main category (the first segment).

/** Answers from the setup survey that shape the starting categories. */
export interface Profile {
  housing: "own" | "rent" | "family" | null;
  vehicle: "none" | "owned" | "financed" | "leased" | null;
  pay: { frequency: PayFrequency; amount: number } | null;
}

export type PayFrequency = "weekly" | "biweekly" | "semimonthly" | "monthly";

/** A category someone added. Its id is `${parent}/c-<random>`. */
export interface CustomCategory {
  id: string;
  name: string;
}

interface Node {
  id: string;
  /** Shown only when this returns true for the survey answers. Missing answers show everything. */
  when?: (p: Profile) => boolean;
  children?: Node[];
}

const owns = (p: Profile) => p.housing === null || p.housing === "own";
const rents = (p: Profile) => p.housing === null || p.housing === "rent";
const drives = (p: Profile) => p.vehicle !== "none";
const borrows = (p: Profile) => p.vehicle === null || p.vehicle === "financed" || p.vehicle === "leased";

const UTILITIES: Node = {
  id: "housing/utilities",
  when: (p) => p.housing !== "family",
  children: ["electricity", "gas", "water", "internet", "phone"].map((c) => ({ id: `housing/utilities/${c}` })),
};

/** Built-in categories, in display order. Message keys are `cat.<id>`. */
export const TREE: readonly Node[] = [
  {
    id: "housing",
    children: [
      { id: "housing/mortgage", when: owns, children: [{ id: "housing/mortgage/interest" }] },
      { id: "housing/property-tax", when: owns },
      { id: "housing/home-insurance", when: owns },
      { id: "housing/condo-fees", when: owns },
      { id: "housing/upkeep", when: owns },
      { id: "housing/repairs", when: owns },
      { id: "housing/rent", when: rents },
      { id: "housing/tenant-insurance", when: rents },
      { id: "housing/parking", when: rents },
      UTILITIES,
    ],
  },
  {
    id: "transport",
    children: [
      { id: "transport/loan", when: (p) => drives(p) && borrows(p), children: [{ id: "transport/loan/interest" }] },
      { id: "transport/maintenance", when: drives },
      { id: "transport/repairs", when: drives },
      { id: "transport/insurance", when: drives },
      { id: "transport/licensing", when: drives },
      { id: "transport/fuel", when: drives },
      { id: "transport/parking", when: drives },
      { id: "transport/transit" },
      { id: "transport/rideshare" },
    ],
  },
  { id: "groceries" },
  { id: "dining", children: [{ id: "dining/restaurants" }, { id: "dining/coffee" }, { id: "dining/takeout" }] },
  { id: "health", children: [{ id: "health/prescriptions" }, { id: "health/dental" }, { id: "health/vision" }, { id: "health/fitness" }] },
  { id: "shopping", children: [{ id: "shopping/clothing" }, { id: "shopping/electronics" }, { id: "shopping/gifts" }] },
  { id: "household", children: [{ id: "household/furniture" }, { id: "household/supplies" }, { id: "household/pets" }] },
  { id: "entertainment", children: [{ id: "entertainment/subscriptions" }, { id: "entertainment/events" }, { id: "entertainment/hobbies" }] },
  { id: "other" },
  { id: "savings" },
  // Set aside for large purchases. Its budget line is kept by the Purchases page.
  { id: "purchases" },
];

/** Main categories that hold budget lines, in order. */
export const MAIN_CATEGORIES = TREE.map((n) => n.id);

/** Not spending: money coming in, moves between your own accounts, and saving. */
/** The part of a payment that others in a group owe back (see groups.ts). Not spending. */
export const FRONTED = "fronted";

export const NOT_SPENDING = new Set(["income", "transfers-in", "savings", "purchases", FRONTED]);

/** Categories outside the budget tree. */
export const SPECIAL_CATEGORIES = ["income", "transfers-in"] as const;

/** Every built-in id, for message-key checks. */
export function builtInIds(): string[] {
  const out: string[] = [];
  const walk = (nodes: readonly Node[]) => {
    for (const n of nodes) {
      out.push(n.id);
      if (n.children) walk(n.children);
    }
  };
  walk(TREE);
  return [...out, ...SPECIAL_CATEGORIES, "uncategorized"];
}

/** The deepest a category can go: main, sub, and one more. */
export const MAX_DEPTH = 3;

export const depth = (id: string) => id.split("/").length;
export const mainOf = (id: string) => id.split("/")[0]!;
export const parentOf = (id: string): string | null => (id.includes("/") ? id.slice(0, id.lastIndexOf("/")) : null);
export const isCustom = (id: string) => /(^|\/)c-[a-z0-9]+$/.test(id);

/** True when `id` is `ancestor` or sits somewhere under it. */
export function within(id: string, ancestor: string): boolean {
  return id === ancestor || id.startsWith(`${ancestor}/`);
}

export interface Category {
  id: string;
  depth: number;
  children: Category[];
}

/**
 * The tree as it applies to this household: built-in categories the survey
 * rules in, plus custom ones, plus anything already in use (so answering
 * the survey never hides a category that holds data).
 */
export function categoryTree(profile: Profile | null, custom: readonly CustomCategory[], inUse: ReadonlySet<string> = new Set()): Category[] {
  const answers: Profile = profile ?? { housing: null, vehicle: null, pay: null };
  const used = (id: string) => [...inUse].some((u) => within(u, id));
  const build = (nodes: readonly Node[]): Category[] =>
    nodes
      .filter((n) => !n.when || n.when(answers) || used(n.id))
      .map((n) => ({ id: n.id, depth: depth(n.id), children: build(n.children ?? []) }));
  const tree = build(TREE);
  const find = (list: Category[], id: string): Category | undefined => {
    for (const c of list) {
      if (c.id === id) return c;
      const hit = find(c.children, id);
      if (hit) return hit;
    }
    return undefined;
  };
  // Parents first, so a custom category under another custom one finds its home.
  for (const c of [...custom].sort((a, b) => depth(a.id) - depth(b.id))) {
    const parent = find(tree, parentOf(c.id) ?? "");
    if (parent && depth(c.id) <= MAX_DEPTH) parent.children.push({ id: c.id, depth: depth(c.id), children: [] });
  }
  return tree;
}

/** Flattens a tree to [category, ...descendants] in display order. */
export function flatten(tree: readonly Category[]): Category[] {
  return tree.flatMap((c) => [c, ...flatten(c.children)]);
}

/** Ids from before categories had sub-categories, and where they live now. */
const RENAMED: Record<string, string> = {
  utilities: "housing/utilities",
  "phone-internet": "housing/utilities",
};

export function migrateCategory(id: string): string;
export function migrateCategory(id: string | null): string | null;
export function migrateCategory(id: string | null): string | null {
  return id === null ? null : (RENAMED[id] ?? id);
}

export function wasRenamed(id: string | null): boolean {
  return id !== null && id in RENAMED;
}

/** A take-home amount per pay period, as a monthly figure in cents. */
export function monthlyPay(pay: NonNullable<Profile["pay"]>): number {
  const perYear = { weekly: 52, biweekly: 26, semimonthly: 24, monthly: 12 }[pay.frequency];
  return Math.round((pay.amount * perYear) / 12);
}

let names = new Map<string, string>();

/** Lets labels find the names of custom categories. Set by the store. */
export function setCustomNames(custom: readonly CustomCategory[]) {
  names = new Map(custom.map((c) => [c.id, c.name]));
}

export function customName(id: string): string | undefined {
  return names.get(id);
}

export function newCustomId(parent: string): string {
  const random = crypto.randomUUID().replace(/-/g, "").slice(0, 10);
  return `${parent}/c-${random}`;
}
